<template>
  <v-container fluid>
    <v-card class="elevation-2">
      <v-toolbar color="brand" dark dense flat>
        <v-toolbar-title>Instructor Bulk Edit</v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <div v-if="loading.fields" class="d-flex justify-center align-center py-5">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <span class="ml-3">Loading available fields...</span>
        </div>

        <v-alert v-else-if="error" type="error" dense>
          {{ error }}
          <v-btn small text @click="fetchFields">Retry</v-btn>
        </v-alert>

        <div v-else>
          
          <div class="mb-4">
            <h3 class="text-h6 mb-2">1. Select Fields to Edit</h3>
            <v-divider class="mb-4"></v-divider>
            
            <v-row align="center" class="mb-2">
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="fieldSearch"
                  prepend-inner-icon="mdi-magnify"
                  label="Search fields..."
                  dense
                  outlined
                  hide-details
                  clearable
                  color="brand"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="8">
                <v-checkbox
                  v-model="selectAll"
                  label="Select All Fields"
                  @change="toggleSelectAll"
                  color="brand"
                  hide-details
                  class="mt-0"
                ></v-checkbox>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col
                v-for="field in filteredFields"
                :key="field.key"
                cols="12"
                sm="6"
                md="3"
              >
                <v-checkbox
                  v-model="selectedFields"
                  :label="field.label"
                  :value="field.key"
                  color="brand"
                  hide-details
                  dense
                ></v-checkbox>
              </v-col>
            </v-row>

            <v-row v-if="filteredFields.length === 0" class="mt-2">
              <v-col cols="12" class="text-center grey--text">
                <v-icon small color="grey" class="mr-1">mdi-alert-circle-outline</v-icon>
                No fields match your search.
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12" class="d-flex justify-end">
                <v-btn
                  color="grey darken-2"
                  @click="openMastersDialog"
                  :loading="loading.masters"
                  outlined
                  class="mr-3"
                >
                  <v-icon left>mdi-book-open-page-variant</v-icon>
                  Masters Data Dictionary
                </v-btn>

                <v-btn
                  :color="selectedFields.length === 0 ? 'grey lighten-1' : 'brand'"
                  :class="{'onBrand--text': selectedFields.length > 0}"
                  @click="downloadTemplate"
                  :loading="loading.download"
                  :disabled="selectedFields.length === 0"
                  elevation="2"
                >
                  <v-icon left>mdi-download</v-icon>
                  Download Excel Template
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <div class="mt-6">
            <h3 class="text-h6 mb-2">2. Upload Edited File</h3>
            <v-divider class="mb-4"></v-divider>
            
            <v-row align="center">
              <v-col cols="12" md="6">
                <v-file-input
                  v-model="uploadFile"
                  label="Select .xlsx File"
                  accept=".xlsx, .xls"
                  outlined
                  dense
                  show-size
                  prepend-icon="mdi-microsoft-excel"
                ></v-file-input>
              </v-col>
              <v-col cols="12" md="2">
                <v-btn
                  color="brand onBrand--text"
                  @click="processUpload"
                  :loading="loading.upload"
                  :disabled="!uploadFile"
                  block
                  dark
                >
                  <v-icon left>mdi-upload</v-icon>
                  Upload & Update
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <div v-if="results.length > 0" class="mt-8">
            <div class="d-flex align-center justify-space-between mb-2">
              <h3 class="text-h6 mb-0">3. Processing Results</h3>
              <v-btn text color="grey darken-1" @click="clearResults" small>
                <v-icon left>mdi-refresh</v-icon> Clear Results
              </v-btn>
            </div>
            <v-divider class="mb-5"></v-divider>
            
            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-4" color="grey lighten-4">
                  <div class="text-overline grey--text text--darken-2">Total Processed</div>
                  <div class="text-h3 font-weight-bold grey--text text--darken-3">{{ results.length }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-4" color="green lighten-5" style="border-color: #A5D6A7 !important;">
                  <div class="text-overline green--text text--darken-3">Successful Updates</div>
                  <div class="text-h3 font-weight-bold green--text text--darken-2">{{ summary.success }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card outlined class="text-center pa-4" color="red lighten-5" style="border-color: #EF9A9A !important;">
                  <div class="text-overline red--text text--darken-3">Failed / Errors</div>
                  <div class="text-h3 font-weight-bold red--text text--darken-2">{{ summary.failed }}</div>
                </v-card>
              </v-col>
            </v-row>

            <v-card outlined>
              <v-card-title class="py-2 px-4 grey lighten-5">
                <span class="text-subtitle-1 font-weight-medium">Detailed Logs</span>
                <v-spacer></v-spacer>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search Code, Status, or Logs..."
                  single-line
                  hide-details
                  dense
                  outlined
                  class="shrink"
                  style="max-width: 350px;"
                ></v-text-field>
              </v-card-title>
              
              <v-divider></v-divider>

              <v-data-table
                :headers="headers"
                :items="results"
                :search="search"
                :items-per-page="10"
                class="elevation-0"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip 
                    :color="getStatusColor(item.status)" 
                    dark 
                    small 
                    class="font-weight-medium"
                  >
                    <v-icon left small v-if="item.status === 'Success'">mdi-check-circle</v-icon>
                    <v-icon left small v-else>mdi-alert-circle</v-icon>
                    {{ item.status }}
                  </v-chip>
                </template>

                <template v-slot:item.reason="{ item }">
                  <span :class="{
                    'red--text text--darken-2 font-weight-medium': item.status === 'Failed' || item.status === 'Error', 
                    'green--text text--darken-2': item.status === 'Success'
                  }">
                    {{ item.reason }}
                  </span>
                </template>
              </v-data-table>
            </v-card>
          </div>

          <v-dialog v-model="showMastersDialog" max-width="1000" scrollable>
            <v-card>
              <v-toolbar color="brand" dark flat>
                <v-icon left>mdi-book-open-page-variant</v-icon>
                <v-toolbar-title class="font-weight-medium">
                  Masters Data Dictionary
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon @click="showMastersDialog = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-toolbar>
              
              <v-card-text class="pa-0">
                <v-tabs vertical v-model="activeTab" background-color="grey lighten-5" color="brand">
                  
                  <v-tab 
                    v-for="(list, title) in mastersData" 
                    :key="'tab-' + title"
                    class="justify-start text-left px-4"
                    style="border-bottom: 1px solid #EEEEEE;"
                  >
                    <div class="d-flex align-center justify-space-between w-100" style="width: 100%;">
                      <span class="text-caption font-weight-bold">{{ title }}</span>
                      <v-chip x-small color="grey lighten-3" class="ml-2 font-weight-bold grey--text text--darken-3">
                        {{ list ? list.length : 0 }}
                      </v-chip>
                    </div>
                  </v-tab>

                  <v-tab-item v-for="(list, title) in mastersData" :key="'content-' + title">
                    <v-card flat class="pa-6" style="height: 65vh; overflow-y: auto; background-color: #FAFAFA;">
                      
                      <div style="position: sticky; top: -24px; background-color: #FAFAFA; z-index: 2; padding-top: 24px; padding-bottom: 8px;">
                        <div class="d-flex align-center justify-space-between mb-4">
                          <h2 class="text-h6 font-weight-bold brand--text mb-0">{{ title }}</h2>
                          <v-btn small text color="brand" @click="copyEntireList(list, title)" v-if="list && list.length > 0">
                            <v-icon left small>mdi-content-copy</v-icon> Copy All
                          </v-btn>
                        </div>
                        
                        <v-text-field
                          v-model="masterSearch"
                          prepend-inner-icon="mdi-magnify"
                          label="Search this list..."
                          dense
                          outlined
                          hide-details
                          class="mb-4"
                          clearable
                          background-color="white"
                        ></v-text-field>
                      </div>

                      <v-row v-if="filteredMasters(list).length > 0">
                        <v-col 
                          v-for="(item, i) in filteredMasters(list)" 
                          :key="i" 
                          cols="12" sm="6" md="4"
                        >
                          <v-card 
                            outlined 
                            class="d-flex justify-space-between align-center px-3 py-2 master-value-card"
                            @click="copyToClipboard(item)"
                            title="Click to copy"
                          >
                            <span class="text-body-2 font-weight-medium text-truncate">{{ item }}</span>
                            <v-icon small color="grey darken-1" class="copy-icon">mdi-content-copy</v-icon>
                          </v-card>
                        </v-col>
                      </v-row>

                      <div v-else class="text-center py-10 grey--text">
                        <v-icon large color="grey lighten-1" class="mb-3">mdi-text-box-search-outline</v-icon>
                        <div>No matches found for "{{ masterSearch }}"</div>
                      </div>

                    </v-card>
                  </v-tab-item>
                </v-tabs>
              </v-card-text>
            </v-card>
          </v-dialog>

          <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2500" top right>
            <v-icon left dark>{{ snackbar.icon }}</v-icon>
            {{ snackbar.text }}
          </v-snackbar>

        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import axios from 'axios';
import * as XLSX from 'xlsx';

export default {
  name: "BulkInstructorEdit",
  data() {
    return {
      // Data State
      allFields: [], 
      selectedFields: [],
      selectAll: false,
      fieldSearch: '', // NEW: State for the fields search bar
      uploadFile: null,
      results: [],
      error: null,
      search: '', 
      
      // Masters Dialog State
      showMastersDialog: false,
      mastersData: {},
      activeTab: 0,
      masterSearch: '',
      snackbar: { 
        show: false, 
        text: '', 
        color: 'success', 
        icon: 'mdi-check-circle' 
      },

      // UI State
      loading: {
        fields: false,
        download: false,
        upload: false,
        masters: false
      },
      
      // Table Headers
      headers: [
        { text: 'Row #', value: 'serial_number', align: 'start', width: '80px' },
        { text: 'Emp Code', value: 'employeecode', width: '150px' },
        { text: 'Status', value: 'status', width: '130px' },
        { text: 'Reason / Logs', value: 'reason' }
      ]
    };
  },
  
  mounted() {
    this.fetchFields();
  },

  computed: {
    summary() {
      const success = this.results.filter(r => r.status === 'Success').length;
      const failed = this.results.filter(r => r.status === 'Failed' || r.status === 'Error').length;
      return { success, failed };
    },
    
    // NEW: Live filtering for the fields checkboxes
    filteredFields() {
      if (!this.fieldSearch) return this.allFields;
      const term = this.fieldSearch.toLowerCase();
      return this.allFields.filter(f => f.label.toLowerCase().includes(term));
    }
  },

  watch: {
    activeTab() {
      this.masterSearch = '';
    }
  },

  methods: {
    async fetchFields() {
      this.loading.fields = true;
      this.error = null;
      try {
        const response = await axios.get('/bulkInstructor/getFields');
        if (response.data && response.data.status === 'SUCCESS') {
          this.allFields = response.data.fields;
        } else {
          this.error = response.data.msg || "Failed to load field configuration.";
        }
      } catch (e) {
        console.error("Error fetching fields:", e);
        this.error = "Network error while fetching fields.";
      } finally {
        this.loading.fields = false;
      }
    },

    toggleSelectAll() {
      if (this.selectAll) {
        // If searching, only select the filtered ones. Otherwise, select all.
        const targetFields = this.fieldSearch ? this.filteredFields : this.allFields;
        
        // Merge without duplicates
        const newSelection = new Set([...this.selectedFields, ...targetFields.map(f => f.key)]);
        this.selectedFields = Array.from(newSelection);
      } else {
        if (this.fieldSearch) {
          // Unselect only the currently visible filtered fields
          const filteredKeys = this.filteredFields.map(f => f.key);
          this.selectedFields = this.selectedFields.filter(k => !filteredKeys.includes(k));
        } else {
          this.selectedFields = [];
        }
      }
    },

    async openMastersDialog() {
      if (Object.keys(this.mastersData).length > 0) {
        this.showMastersDialog = true;
        return;
      }

      this.loading.masters = true;
      try {
        const response = await axios.get('/bulkInstructor/getMasters');
        if (response.data && response.data.status === 'SUCCESS') {
          this.mastersData = response.data.data;
          this.showMastersDialog = true;
        } else {
          alert(response.data.msg || "Failed to load Masters reference.");
        }
      } catch (error) {
        console.error("Failed to load masters", error);
        alert("Network error while loading Masters reference.");
      } finally {
        this.loading.masters = false;
      }
    },

    filteredMasters(list) {
      if (!list) return [];
      if (!this.masterSearch) return list;
      const term = this.masterSearch.toLowerCase();
      return list.filter(item => item.toString().toLowerCase().includes(term));
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.snackbar = {
          show: true,
          text: `Copied "${text}" to clipboard!`,
          color: 'green darken-2',
          icon: 'mdi-check-circle'
        };
      } catch (err) {
        this.snackbar = { show: true, text: 'Failed to copy text.', color: 'red darken-2', icon: 'mdi-alert' };
      }
    },

    async copyEntireList(list, title) {
      try {
        await navigator.clipboard.writeText(list.join('\n'));
        this.snackbar = {
          show: true,
          text: `Copied all ${list.length} items from ${title}!`,
          color: 'brand',
          icon: 'mdi-format-list-checks'
        };
      } catch (err) {
        this.snackbar = { show: true, text: 'Failed to copy list.', color: 'red darken-2', icon: 'mdi-alert' };
      }
    },

    downloadTemplate() {
      if (this.selectedFields.length === 0) return;
      
      this.loading.download = true;
      try {
        const headers = ["Employee Code"];
        
        this.selectedFields.forEach(key => {
          const field = this.allFields.find(f => f.key === key);
          if (field) headers.push(field.label);
        });

        const worksheet = XLSX.utils.aoa_to_sheet([headers]);
        
        const colWidths = headers.map(h => ({ wch: h.length + 10 }));
        worksheet['!cols'] = colWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Instructors");
        
        XLSX.writeFile(workbook, "Instructor_Bulk_Update_Template.xlsx");

      } catch (error) {
        console.error("Download failed", error);
        alert("An error occurred while creating the template.");
      } finally {
        this.loading.download = false;
      }
    },

    async processUpload() {
      if (!this.uploadFile) return;

      this.loading.upload = true;
      this.results = [];
      this.search = '';

      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

          if (jsonData.length === 0) {
            alert("The uploaded file is empty or unreadable.");
            this.loading.upload = false;
            return;
          }

          const response = await axios.post('/bulkInstructor/uploadBulkData', { 
            data: jsonData 
          });

          if (Array.isArray(response.data)) {
            this.results = response.data;
          } else {
            this.results = response.data.result || [];
            if(response.data.status === 'NO') alert(response.data.msg);
          }
          
          this.uploadFile = null; 

        } catch (error) {
          console.error("Upload processing failed", error);
          this.results = [{
            serial_number: 0,
            employeecode: 'N/A',
            status: 'Error',
            reason: 'Could not process file. Ensure it is a valid .xlsx file.'
          }];
        } finally {
          this.loading.upload = false;
        }
      };

      reader.readAsArrayBuffer(this.uploadFile);
    },

    clearResults() {
      this.results = [];
      this.search = '';
    },

    getStatusColor(status) {
      if (status === 'Success') return 'green darken-1';
      if (status === 'Failed' || status === 'Error') return 'red darken-1';
      return 'grey darken-1';
    }
  }
};
</script>

<style scoped>

.master-value-card {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background-color: #ffffff;
}

.master-value-card .copy-icon {
  opacity: 0.2;
  transition: all 0.2s ease;
}

.master-value-card:hover {
  border-color: var(--v-brand-base, #1976D2) !important;
  background-color: #F0F6FF;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important;
}

.master-value-card:hover .copy-icon {
  opacity: 1;
  color: var(--v-brand-base, #1976D2) !important;
}
</style>